import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Errors } from "../requests/controls";
import {
  locationJobFiscalDocumentsPath,
  locationJobFiscalDocumentPath,
} from "@/routes";

type Parties = {
  issuer: Record<string, string>;
  customer: Record<string, string>;
  fiscal?: Record<string, string>;
};
export default function FiscalForm({
  job_id,
  return_page,
  revision,
  document_id,
  input,
  fiscal_fields,
  provider,
}: {
  job_id: string;
  return_page?: number | null;
  revision: string;
  document_id: string | null;
  input: Parties;
  fiscal_fields: string[];
  provider: string | null;
}) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const list = locationJobFiscalDocumentsPath(auth.location.id, job_id, {
    page: return_page,
    document_id: return_page ? document_id : undefined,
  });
  return (
    <JobWorkspace
      primaryAction={false}
      breadcrumbs={[{ title: t("fiscal_documents.title"), href: list }]}
    >
      <Head title={t("fiscal_documents.prepare")} />
      <PageCanvas padded={false}>
        <h2 className="text-page font-display">
          {t("fiscal_documents.prepare")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("fiscal_documents.draft_help")}
        </p>
        <Form
          action={
            document_id
              ? locationJobFiscalDocumentPath(
                  auth.location.id,
                  job_id,
                  document_id,
                )
              : list
          }
          method={document_id ? "patch" : "post"}
        >
          {({ processing }) => (
            <div className="space-y-6">
              {return_page && (
                <input type="hidden" name="return_page" value={return_page} />
              )}
              <Errors
                targets={{
                  issuer: "fiscal-issuer-name",
                  customer: "fiscal-customer-name",
                  fiscal: "fiscal-evidence-vat_ron",
                }}
              />
              <input
                type="hidden"
                name="fiscal_document[revision]"
                value={revision}
              />
              <p role="status" className="text-muted-foreground text-sm">
                {provider
                  ? `${t("fiscal_documents.provider")}: ${t(`fiscal_documents.${provider}`)}`
                  : t("fiscal_documents.connection_pending")}
              </p>
              {(["issuer", "customer"] as const).map((group) => (
                <fieldset
                  key={group}
                  className="space-y-4 rounded-lg border p-4"
                >
                  <legend className="px-2 font-medium">
                    {t(`fiscal_documents.${group}`)}
                  </legend>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {[
                      "name",
                      "country",
                      "address",
                      "city",
                      "region",
                      "postal_code",
                      "tax_identifier",
                      "email",
                    ].map((name) => (
                      <div className="space-y-2" key={name}>
                        <Label htmlFor={`fiscal-${group}-${name}`}>
                          {t(`fiscal_documents.${name}`)}
                        </Label>
                        <Input
                          id={`fiscal-${group}-${name}`}
                          name={`fiscal_document[${group}][${name}]`}
                          defaultValue={input[group]?.[name] || ""}
                          aria-invalid={!!errors?.[group]}
                          maxLength={500}
                        />
                        {errors?.[group] && name === "name" && (
                          <p className="text-sm text-destructive">
                            {String(errors[group])}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </fieldset>
              ))}
              {fiscal_fields.length > 0 && (
                <fieldset className="space-y-4 rounded-lg border p-4">
                  <legend className="px-2 font-medium">
                    {t("fiscal_documents.fiscal")}
                  </legend>
                  {fiscal_fields.map((name) => (
                    <div key={name} className="space-y-2">
                      <Label htmlFor={`fiscal-evidence-${name}`}>
                        {t(`fiscal_documents.${name}`)}
                      </Label>
                      <Input
                        id={`fiscal-evidence-${name}`}
                        name={`fiscal_document[fiscal][${name}]`}
                        defaultValue={input.fiscal?.[name] || ""}
                        inputMode={name === "vat_ron" ? "decimal" : undefined}
                        maxLength={500}
                      />
                    </div>
                  ))}
                </fieldset>
              )}
              <Button type="submit" disabled={processing}>
                {t("fiscal_documents.save")}
              </Button>
            </div>
          )}
        </Form>
        <Link href={list} className="block underline">
          {t("inventory.cancel")}
        </Link>
      </PageCanvas>
    </JobWorkspace>
  );
}
